// core/features/campaigns/api.ts
import { api } from "core/axios/axios";
import {
  ICampaign,
  ICampaignListParams,
  ICampaignListResult,
  IRawCampaignsResponse,
  ITrashCampaign,
} from "./types";

function toQuery(params: ICampaignListParams): string {
  const qs = new URLSearchParams();
  qs.set("paginator", String(Math.max(0, (params.page ?? 1) - 1)));

  for (const [k, v] of Object.entries(params)) {
    if (k === "page") continue;
    if (v !== undefined && v !== null && v !== "") {
      qs.set(k, String(v));
    }
  }
  return qs.toString();
}

function normalizeCampaignDetails(
  input: IRawCampaignsResponse["campaign_details"]
): ICampaign[] {
  if (Array.isArray(input)) return input;
  if (input && typeof input === "object") {
    return Object.values(input) as ICampaign[];
  }
  return [];
}

export async function fetchCampaigns<T extends ICampaign = ICampaign>(
  params: ICampaignListParams
): Promise<ICampaignListResult<T>> {
  const q = toQuery(params);
  const { data } = await api.get<IRawCampaignsResponse>(`campaigns?${q}`);

  console.log("Fetched Campaigns Data:", data);

  const items = normalizeCampaignDetails(data.campaign_details) as T[];
  return {
    items,
    total: data.camp_count ?? 0,
    page: params.page ?? 1,
  };
}



// Campaign Creation

export async function createCampaignsRes(body: any) {
  return api.post("campaigns/unary", body);
}


/**
 * The request body `POST campaigns/ab-testing` accepts.
 *
 * Deliberately not the unary campaign shape: an A/B campaign has no top-level
 * `tid`/`subject`, because each variant carries its own.
 */
export interface IABCampaignBody {
    name: string;
    /** `YYYY-MM-DD`; a full ISO string works, the server reads the first ten characters. */
    sdate: string;
    edate: string;
    cgrp: number[];
    mailToUse: string;
    friendlyName: string;
    /** Share of the audience that gets the test, split evenly between A and B. */
    testPercentage: number;
    /** Hours to wait after the test send before picking a winner. */
    evaluationHours: number;
    winnerMetric: "opens" | "clicks";
    variantA: { tid: number; subject: string };
    variantB: { tid: number; subject: string };
}

/**
 * Create an A/B campaign.
 *
 * A sibling of `campaigns/unary`: same base URL, same Cognito header from the
 * shared `api` instance. The 200 body is the JSON **string**
 * `"A/B campaign saved successfully"` and carries no campaign id, so the caller
 * has to refetch the list to see what was created.
 */
export async function createABCampaignRes(body: IABCampaignBody) {
    return api.post("campaigns/ab-testing", body);
}

/**
 * The status a just-saved campaign actually got.
 *
 * The A/B save answers 200 with a bare string and no id, and a save made with
 * too few credits is still a 200 — the campaign is simply stored as `PAUSED`
 * and nothing is deducted. So "created" on its own can be a lie, and the only
 * way to tell is to look the campaign up again by the name it was saved under.
 *
 * Returns null when it cannot be found, which is not the same as a failure:
 * the save said it worked, so the caller should not turn a lookup miss into an
 * error. (A `campaign_id` in the save response would remove all of this; it is
 * the second item of the backend handoff.)
 */
export async function campaignStatusByName(name: string): Promise<string | null> {
    try {
        const { items } = await fetchCampaigns({ page: 1 });
        const wanted = name.trim().toLowerCase();
        const match = items.find((c) => String(c.name ?? "").trim().toLowerCase() === wanted);
        if (!match) return null;
        // Normalised lists use `c_status`; the raw row calls it `campaign_status`.
        const status = match.c_status ?? (match as Record<string, unknown>).campaign_status;
        return typeof status === "string" ? status : null;
    } catch {
        // A failed lookup must not turn a successful save into an error.
        return null;
    }
}

/**
 * The message an A/B save failed with.
 *
 * This endpoint answers with a bare JSON string rather than an error object —
 * `"Campaign name cannot be longer than 50 characters"` — so the usual
 * `error.message` is the transport's, not the server's. 400 and 424 bodies are
 * written for people and are shown as they are; anything else is not.
 */
export function abCampaignErrorMessage(error: any): string | null {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status !== 400 && status !== 424) return null;
    if (typeof body === "string" && body.trim()) return body.trim();
    // Some gateways hand back the string already parsed into `{ message }`.
    if (typeof body?.message === "string" && body.message.trim()) return body.message.trim();
    return null;
}

export async function updateCampaigns(body: any) {
  return api.put("campaigns/unary", body);
}

export async function getCampaignsByIdRes(id: number | string) {
  return api.get("campaigns/unary?cid=" + id);
}

export async function deleteCampaign(id: number | string) {
  return api.delete("campaigns/unary?cid=" + id);
}


/** GET /campaign-trash — fetch all soft-deleted campaigns */
export async function getTrashCampaign(): Promise<ITrashCampaign[]> {
  const { data } = await api.get("campaign-trash");
  const trashed = data?.Trashed_campaigns;
  
  if (!trashed) return [];
  if (Array.isArray(trashed)) return trashed;
  if (typeof trashed === "object") return Object.values(trashed) as ITrashCampaign[];
  return [];
}

/** PUT /campaign-trash/{cid} — restore a soft-deleted campaign */
export async function restoreTrashedCampaign(id: number | string): Promise<any> {
  const { data } = await api.put(`campaign-trash/${id}`);
  return data;
}
